import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';

interface ApplicationLoadingContextType {
  isApplicationLoading: boolean;
  setApplicationLoading: (loading: boolean) => void;
  hasReceivedServerResponse: boolean;
  setHasReceivedServerResponse: (received: boolean) => void;
  shouldShowLoadingScreen: boolean;
}

const ApplicationLoadingContext = createContext<ApplicationLoadingContextType | undefined>(undefined);

export const useApplicationLoading = (): ApplicationLoadingContextType => {
  const context = useContext(ApplicationLoadingContext);
  if (!context) {
    throw new Error('useApplicationLoading must be used within an ApplicationLoadingProvider');
  }
  return context;
};

interface ApplicationLoadingProviderProps {
  children: React.ReactNode;
}

export const ApplicationLoadingProvider: React.FC<ApplicationLoadingProviderProps> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [isApplicationLoading, setApplicationLoading] = useState(false);
  const [hasReceivedServerResponse, setHasReceivedServerResponse] = useState(false);
  const [lastUserId, setLastUserId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [expectedNextStep, setExpectedNextStep] = useState<string | null>(null);

  // Initialize state from sessionStorage to persist across page navigations
  useEffect(() => {
    const storedUserId = sessionStorage.getItem('lastUserId');
    const storedHasResponse = sessionStorage.getItem('hasReceivedServerResponse') === 'true';
    const storedExpectedNextStep = sessionStorage.getItem('expectedNextStep');
    
    if (storedUserId) {
      console.log('🔄 ApplicationLoadingContext: Restoring state from sessionStorage', {
        storedUserId,
        storedHasResponse,
        storedExpectedNextStep
      });
      setLastUserId(storedUserId);
      setHasReceivedServerResponse(storedHasResponse);
      setExpectedNextStep(storedExpectedNextStep);
    }
    setIsInitialized(true);
  }, []);

  // Check if current URL matches the expected next step
  const isOnExpectedPage = () => {
    if (!expectedNextStep) return true; // No expected step, don't block
    
    const currentPath = window.location.pathname;
    
    // Extract path from expectedNextStep (it might be a full URL)
    let expectedPath;
    try {
      if (expectedNextStep.startsWith('http')) {
        expectedPath = new URL(expectedNextStep).pathname;
      } else {
        expectedPath = expectedNextStep;
      }
    } catch {
      expectedPath = expectedNextStep;
    }
    
    console.log('🔍 Checking if on expected page:', {
      currentPath,
      expectedPath,
      expectedNextStep,
      matches: currentPath === expectedPath
    });
    
    return currentPath === expectedPath;
  };

  // Check if user is on a public route (login, registration, etc.)
  const isOnPublicRoute = () => {
    const currentPath = window.location.pathname.toLowerCase();
    const publicRoutePatterns = [
      '/login',
      '/registration',
      '/guestlogin',
      '/securelogin',
      '/recoverpassword',
      '/auth/callback',
      '/auth/magic-link',
      '/access/'
    ];
    return publicRoutePatterns.some(pattern => currentPath.includes(pattern));
  };

  // Simple check if we should show loading screen
  const shouldShowLoadingScreen = (() => {
    // Don't show loading if not initialized yet
    if (!isInitialized) return false;
    
    // Don't show loading if auth is still loading
    if (authLoading) return false;
    
    // Don't show loading if user is not authenticated
    if (!user) return false;
    
    // Don't show loading screen on public routes (login, registration, etc.)
    // This prevents stale sessionStorage from blocking access to login pages
    if (isOnPublicRoute()) {
      console.log('🔄 On public route, skipping loading screen check');
      return false;
    }
    
    // Show loading if we're waiting for server response
    if (isApplicationLoading && !hasReceivedServerResponse) return true;
    
    // Show loading if we've received server response but haven't navigated to expected page yet
    if (hasReceivedServerResponse && expectedNextStep && !isOnExpectedPage()) {
      console.log('🔄 Showing loading screen - waiting for navigation to expected page');
      return true;
    }
    
    const result = false;
    
    // Debug logging
    console.log('🔍 ApplicationLoadingContext shouldShowLoadingScreen:', {
      user: !!user,
      userId: user?.id,
      authLoading,
      isApplicationLoading,
      hasReceivedServerResponse,
      isInitialized,
      expectedNextStep,
      isOnExpectedPage: isOnExpectedPage(),
      result
    });
    
    return result;
  })();

  // Reset state when user changes or logs out
  useEffect(() => {
    // Don't process until we're initialized
    if (!isInitialized) return;
    
    console.log('🔄 ApplicationLoadingContext user effect:', {
      user: !!user,
      userId: user?.id,
      lastUserId,
      authLoading,
      isNewUser: user && user.id !== lastUserId
    });
    
    if (!user && !authLoading) {
      // User logged out (and auth is not loading) - reset everything
      console.log('🔄 User logged out, resetting loading state');
      setApplicationLoading(false);
      setHasReceivedServerResponse(false);
      setLastUserId(null);
      setExpectedNextStep(null);
      // Clear sessionStorage
      sessionStorage.removeItem('lastUserId');
      sessionStorage.removeItem('hasReceivedServerResponse');
      sessionStorage.removeItem('expectedNextStep');
    } else if (user && user.id !== lastUserId) {
      // Check if this is truly a new login or just a page reload
      const storedUserId = sessionStorage.getItem('lastUserId');
      const storedHasResponse = sessionStorage.getItem('hasReceivedServerResponse') === 'true';
      const storedExpectedNextStep = sessionStorage.getItem('expectedNextStep');
      
      if (storedUserId === user.id && storedHasResponse) {
        // This is a page reload, not a new login - restore previous state
        console.log('🔄 Page reload detected, restoring previous state for user:', user.id);
        setLastUserId(user.id);
        setHasReceivedServerResponse(true);
        setApplicationLoading(false);
        setExpectedNextStep(storedExpectedNextStep);
      } else {
        // This is a genuine new user login - start loading
        console.log('🔄 New user login detected, starting application loading...', user.id);
        setApplicationLoading(true);
        setHasReceivedServerResponse(false);
        setLastUserId(user.id);
        setExpectedNextStep(null);
        // Store in sessionStorage
        sessionStorage.setItem('lastUserId', user.id);
        sessionStorage.removeItem('hasReceivedServerResponse');
        sessionStorage.removeItem('expectedNextStep');
      }
    }
  }, [user, lastUserId, authLoading, isInitialized]);

  // Listen for server response received event
  useEffect(() => {
    const handleServerResponse = (event: CustomEvent) => {
      console.log('🎉 ApplicationLoadingContext: Server response received', event.detail);
      
      const nextStep = event.detail?.nextStep;
      if (nextStep) {
        setExpectedNextStep(nextStep);
        sessionStorage.setItem('expectedNextStep', nextStep);
        console.log('🎯 Expected next step set to:', nextStep);
      }
      
      setHasReceivedServerResponse(true);
      setApplicationLoading(false);
      // Store in sessionStorage
      sessionStorage.setItem('hasReceivedServerResponse', 'true');
    };

    console.log('🔄 ApplicationLoadingContext: Setting up serverResponseReceived listener');
    window.addEventListener('serverResponseReceived', handleServerResponse as EventListener);

    return () => {
      console.log('🔄 ApplicationLoadingContext: Removing serverResponseReceived listener');
      window.removeEventListener('serverResponseReceived', handleServerResponse as EventListener);
    };
  }, []);

  // Listen for route changes to detect when we've reached the expected page
  useEffect(() => {
    if (expectedNextStep && hasReceivedServerResponse) {
      const checkNavigation = () => {
        if (isOnExpectedPage()) {
          console.log('🎯 Reached expected page, clearing expected next step');
          setExpectedNextStep(null);
          sessionStorage.removeItem('expectedNextStep');
        }
      };

      // Check immediately
      checkNavigation();
      
      // Also check periodically in case the URL changes
      const interval = setInterval(checkNavigation, 100);
      
      return () => clearInterval(interval);
    }
  }, [expectedNextStep, hasReceivedServerResponse]);

  const value: ApplicationLoadingContextType = {
    isApplicationLoading,
    setApplicationLoading,
    hasReceivedServerResponse,
    setHasReceivedServerResponse,
    shouldShowLoadingScreen
  };

  return (
    <ApplicationLoadingContext.Provider value={value}>
      {children}
    </ApplicationLoadingContext.Provider>
  );
}; 